<template>
  <div
    class="bg-white rounded-xl px-4 py-6 sm:p-8 text-gray-900 text-sm xl:max-w-sm"
  >
    <div class="text-2xl font-bold">
      {{ $t('tickets.billing_details') }}
    </div>
    <div v-if="isPaid" class="mt-4">
      <BaseLink
        v-if="
          order.payment_system_data?.receipt_url &&
          order.payment_system_type === PaymentSystem.CloudPayments
        "
        :href="order.payment_system_data.receipt_url"
        class="text-md"
        target="_blank"
      >
        <BaseIcon name="outline_archive" class="mr-1"></BaseIcon>
        {{ $t('tickets.order_receipt') }}
      </BaseLink>
      <BaseButton
        v-else-if="order.payment_system_type === PaymentSystem.Stripe"
        size="sm"
        class="w-auto flex"
        @click="sendReceipt"
      >
        <BaseIcon name="outline_archive" class="mr-1" size="sm"></BaseIcon>
        {{ $t('tickets.send_receipt') }}
      </BaseButton>
    </div>
    <div class="pb-2 mt-4">
      {{ $t('tickets.billing_details_description') }}
    </div>
    <div
      v-if="userEmail"
      class="flex flex-wrap gap-x-2 pb-6 border-b-2 border-primary-100"
    >
      <div class="font-bold whitespace-nowrap">
        {{ $t('profile.email') }}
      </div>
      <div>
        {{ userEmail }}
      </div>
    </div>
    <div class="py-6 border-b-2 border-primary-100">
      <div class="text-lg font-bold pb-2">
        {{ $t('tickets.order_summary') }}
      </div>
      <div class="pb-6">
        {{ $t('tickets.order_summary_description') }}
      </div>
      <div class="grid gap-2">
        <div class="grid grid-cols-2 gap-4 font-bold">
          <div v-if="isPackagesOrder(order)">
            {{ $t('packages.cart_title') }}
          </div>
          <div v-else>{{ $t('tickets.ticket') }}</div>
          <div class="text-right">{{ $t('tickets.price') }}</div>
        </div>
        <template
          v-for="(ticketId, index) in Object.keys(orderItemsInfo)"
          :key="ticketId"
        >
          <div class="grid grid-cols-2 gap-4">
            <div>
              <span v-if="Object.keys(orderItemsInfo).length > 1">
                {{ `${index + 1} ` }}
              </span>
              <span>
                {{ orderItemsInfo[ticketId].name }}
              </span>
              <span v-if="orderItemsInfo[ticketId].quantity > 1">
                ({{ orderItemsInfo[ticketId].quantity }})
              </span>
            </div>
            <div class="text-right whitespace-nowrap">
              {{
                formatPrice(
                  orderItemsInfo[ticketId].price / 100,
                  order.currency,
                  languageMap[locale]
                )
              }}
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="grid gap-2 pt-6">
      <div v-if="order.promo_code_code" class="grid grid-cols-2">
        <div>{{ $t('tickets.promo_code') }}</div>
        <div class="text-right whitespace-nowrap">
          {{ order.promo_code_code }}
        </div>
      </div>
      <div v-if="order.promo_code_discount" class="grid grid-cols-2">
        <div>{{ $t('tickets.discount') }}</div>
        <div class="text-right whitespace-nowrap">
          <template v-if="order.promo_code_discount">
            -{{
              formatPrice(
                order.promo_code_discount / 100,
                order.currency,
                languageMap[locale]
              )
            }}
          </template>
          <template v-else>0</template>
        </div>
      </div>
      <div v-if="order.type === OrderType.Upgrade" class="grid grid-cols-2">
        <div>
          {{ $t('tickets.upgrade_price') }}
        </div>
        <div class="text-right whitespace-nowrap">
          {{
            formatPrice(order.amount / 100, order.currency, languageMap[locale])
          }}
        </div>
      </div>
      <div class="grid grid-cols-2">
        <div class="font-bold">
          {{ $t('tickets.total') }}
        </div>
        <div
          class="text-right whitespace-nowrap"
          :class="order.special_price && 'line-through'"
        >
          {{
            formatPrice(order.amount / 100, order.currency, languageMap[locale])
          }}
        </div>
      </div>
      <div v-if="order.special_price" class="flex gap-3 justify-between">
        <div class="font-bold">
          {{ $t('tickets.special_price') }}
        </div>
        <div class="text-right whitespace-nowrap">
          {{
            formatPrice(
              order.special_price / 100,
              order.currency,
              languageMap[locale]
            )
          }}
        </div>
      </div>
      <div
        v-if="isOrderPayable"
        class="flex justify-between mb-3 text-xs text-left text-gray-600"
      >
        {{ $t('tickets.vat_disclaimer') }}
      </div>
    </div>
    <div v-if="isOrderPayable" class="flex flex-col items-end pt-6">
      <div>
        <BaseButton :loading="loading" @click="emit('pay')">
          {{ $t('tickets.go_to_payment') }}
        </BaseButton>
      </div>
      <i18n-t
        keypath="tickets.disclaimer"
        tag="div"
        class="text-gray-500 text-xs text-right mt-3"
      >
        <template #terms>
          <BaseLink href="https://wnhub.io/terms-conditions" target="_blank">
            {{ $t('tickets.disclaimer_terms') }}
          </BaseLink>
        </template>
        <template #br><br /></template>
        <template #privacy>
          <BaseLink href="https://wnhub.io/privacy-policy" target="_blank">
            {{ $t('tickets.disclaimer_privacy') }}
          </BaseLink>
        </template>
      </i18n-t>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { OrderResponse } from '~/models/orders'
import { OrderStatus, OrderType, OrderPaymentType } from '~/models/orders'
import { PaymentSystem } from '~/models/payment'
import { useOrders } from '~/stores/orders'
import { languageMap } from '~/models/locale'
import { useNotifications } from '~/stores/notifications'

interface PropsInterface {
  order: OrderResponse
  userEmail?: string
  loading?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  userEmail: '',
  loading: false,
})

const emit = defineEmits(['pay'])

const { formatPrice, resendStripeReceipt, isPackagesOrder } = useOrders()
const { locale, t } = useI18n()
const { pushNotification } = useNotifications()

const isOrderPayable = computed(
  () =>
    props.order?.status === OrderStatus.Created &&
    [OrderPaymentType.Card].includes(props.order?.payment_type)
)

const isPaid = computed(() => props.order?.status === OrderStatus.Done)

async function sendReceipt() {
  try {
    await resendStripeReceipt(props.order.id)
    pushNotification({
      title: t(`notifications.tickets.send_receipt_success`),
    })
  } catch (error: any) {
    pushNotification({
      title: t(`notifications.tickets.send_receipt_error`),
      theme: 'destructive',
    })
    useLogError(error)
  }
}

const orderItemsInfo = computed(() => {
  return props.order.items.reduce((acc, item) => {
    if (item.entity_id in acc) {
      acc[item.entity_id].quantity += 1
      acc[item.entity_id].price += item.amount
    } else {
      const ticketName =
        'ticket' in item.details ? item.details.ticket?.name : ''

      const packageName =
        'sponsor_package' in item.details
          ? item.details.sponsor_package?.name
          : ''

      acc[item.entity_id] = {
        name: ticketName || packageName || item.entity_id.toString(),
        quantity: 1,
        price: item.amount,
      }
    }

    return acc
  }, {} as Record<string, { name: string; quantity: number; price: number }>)
})
</script>
